import { all, call, put, takeEvery } from 'redux-saga/effects'

import { referenceApi } from '../../api/referenceApi'
import {
    DATABASE_CREATE_ROW,
    DATABASE_DELETE_ROW,
    DATABASE_LOAD_OPTIONS,
    DATABASE_LOAD_TABLE,
    DATABASE_UPDATE_ROW,
} from '../actions/database'
import { databaseActions } from '../slices/database'

const normalizeList = (data) => (Array.isArray(data) ? data : [data])

function buildDeletePath(table, row) {
    if (table === 'current_streams') {
        return `/reference/current_streams?schedule_id=${row.schedule_id}&group_id=${row.group_id}`
    }

    if (table === 'curriculum') {
        return `/reference/curriculum?workload_id=${row.workload_id}&group_id=${row.group_id}`
    }

    const idKey = Object.keys(row).find((key) => key === 'id' || key.endsWith('_id'))
    const id = idKey ? row[idKey] : ''

    return `/reference/${encodeURIComponent(table)}/${encodeURIComponent(id)}`
}

function* databaseLoadTableSaga(action) {
    const table = action.payload

    try {
        yield put(databaseActions.setTable(table))
        yield put(databaseActions.setLoading(true))
        yield put(databaseActions.setError(null))

        const data = yield call(referenceApi.getTable, table)
        yield put(databaseActions.setTableData(normalizeList(data)))
    } catch (error) {
        console.error(`DATABASE TABLE ERROR (${table}):`, error)
        yield put(databaseActions.setError(
            error instanceof Error ? error.message : `Не удалось загрузить таблицу ${table}`
        ))
        yield put(databaseActions.setTableData([]))
    } finally {
        yield put(databaseActions.setLoading(false))
    }
}

function* databaseLoadOptionsSaga(action) {
    const tables = action.payload

    if (!tables?.length) {
        yield put(databaseActions.setReferenceOptions({}))
        return
    }

    try {
        yield put(databaseActions.setReferencesLoading(true))
        yield put(databaseActions.setAddError(null))

        const entries = yield all(
            tables.map((table) => call(function* loadTableOption() {
                const data = yield call(referenceApi.getTable, table)
                return [table, normalizeList(data)]
            }))
        )

        yield put(databaseActions.setReferenceOptions(Object.fromEntries(entries)))
    } catch (error) {
        console.error('DATABASE OPTIONS ERROR:', error)
        yield put(databaseActions.setAddError(
            error instanceof Error ? error.message : 'Ошибка при загрузке справочников'
        ))
        yield put(databaseActions.setReferenceOptions({}))
    } finally {
        yield put(databaseActions.setReferencesLoading(false))
    }
}

function* databaseCreateRowSaga(action) {
    const { table, data } = action.payload

    try {
        yield put(databaseActions.setAdding(true))
        yield put(databaseActions.setAddError(null))

        yield call(referenceApi.createRow, table, data)
        yield call(databaseLoadTableSaga, { payload: table })
        yield call(databaseLoadOptionsSaga, { payload: action.payload.referenceTables || [] })
    } catch (error) {
        console.error('DATABASE CREATE ERROR:', error)
        yield put(databaseActions.setAddError(
            error instanceof Error ? error.message : 'Ошибка при добавлении записи'
        ))
    } finally {
        yield put(databaseActions.setAdding(false))
    }
}

function* databaseUpdateRowSaga(action) {
    const { table, id, data } = action.payload

    try {
        yield put(databaseActions.setSaving(true))
        yield put(databaseActions.setEditError(null))

        yield call(referenceApi.updateRow, table, id, data)
        yield call(databaseLoadTableSaga, { payload: table })
    } catch (error) {
        console.error('DATABASE UPDATE ERROR:', error)
        yield put(databaseActions.setEditError(
            error instanceof Error ? error.message : 'Ошибка при сохранении записи'
        ))
    } finally {
        yield put(databaseActions.setSaving(false))
    }
}

function* databaseDeleteRowSaga(action) {
    const { table, row } = action.payload

    try {
        yield put(databaseActions.setDeleting(true))
        yield put(databaseActions.setError(null))

        const path = buildDeletePath(table, row)
        yield call(referenceApi.deleteRow, path)
        yield call(databaseLoadTableSaga, { payload: table })
    } catch (error) {
        console.error('DATABASE DELETE ERROR:', error)
        yield put(databaseActions.setError(
            error instanceof Error ? error.message : 'Ошибка при удалении записи'
        ))
    } finally {
        yield put(databaseActions.setDeleting(false))
    }
}

function* databaseLoadTableWatch() {
    yield takeEvery(DATABASE_LOAD_TABLE, databaseLoadTableSaga)
}

function* databaseLoadOptionsWatch() {
    yield takeEvery(DATABASE_LOAD_OPTIONS, databaseLoadOptionsSaga)
}

function* databaseCreateRowWatch() {
    yield takeEvery(DATABASE_CREATE_ROW, databaseCreateRowSaga)
}

function* databaseUpdateRowWatch() {
    yield takeEvery(DATABASE_UPDATE_ROW, databaseUpdateRowSaga)
}

function* databaseDeleteRowWatch() {
    yield takeEvery(DATABASE_DELETE_ROW, databaseDeleteRowSaga)
}

export default function* databaseRootSaga() {
    yield all([
        databaseLoadTableWatch(),
        databaseLoadOptionsWatch(),
        databaseCreateRowWatch(),
        databaseUpdateRowWatch(),
        databaseDeleteRowWatch(),
    ])
}
